import { DELETION_FLAG, DeletionFlag } from "@packages/core";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuPencil } from "react-icons/lu";
import * as v from "valibot";

import {
  ButtonStyle,
  FormField,
  FormFieldInput,
  FormFieldInputStyle,
  PageName,
  PageNumberSelector,
  PageSizeSelector,
  SearchButton,
  SkeletonScreen,
  TABLE_STYLE,
  TableHeadCell,
  TypographyStyle,
} from "#components";
import { useBedInfiniteQuery, useDebounce, useFacilityQuery } from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/beds/")({
  component: Component,
  validateSearch: v.object({
    pageSize: v.fallback(
      v.optional(
        v.pipe(v.union([v.number(), v.pipe(v.string(), v.toNumber())]), v.integer(), v.minValue(1)),
      ),
      undefined,
    ),
    roomName: v.fallback(v.optional(v.string()), undefined),
  }),
});

function Component() {
  const { facilityKey } = Route.useParams();
  const { pageSize = 100, roomName } = Route.useSearch();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const debounce = useDebounce(500);
  const facilityQuery = useFacilityQuery(facilityKey);
  const [deleted] = useState<DeletionFlag>(DELETION_FLAG.DEFAULT);
  const [descendingOrder, setDescendingOrder] = useState(false);
  const [open, setOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("roomName");
  const [pageNumber, setPageNumber] = useState(0);

  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", deleted],
    ],
    limit: deleted !== DELETION_FLAG.DEFAULT ? pageSize : undefined,
  });

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (
        deleted === DELETION_FLAG.DEFAULT &&
        bedInfiniteQuery.hasNextPage &&
        !bedInfiniteQuery.isFetching
      ) {
        bedInfiniteQuery.fetchNextPage();
      }
    },
    [bedInfiniteQuery, deleted],
  );

  const { beds, numberOfBeds } = useMemo(
    function () {
      const beds =
        bedInfiniteQuery.data?.pages
          .flatMap(function (page) {
            return page.beds;
          })
          .filter(function (bed) {
            if (roomName) {
              return bed.roomName.includes(roomName);
            }
            return true;
          })
          .sort(function (a, b) {
            switch (orderBy) {
              case "roomName":
                return (
                  (a.roomName.localeCompare(b.roomName) ||
                    a.roomPronunciation.localeCompare(b.roomPronunciation) ||
                    a.bedName.localeCompare(b.bedName) ||
                    a.bedPronunciation.localeCompare(b.bedPronunciation)) *
                  (descendingOrder ? -1 : 1)
                );
              case "roomPronunciation":
                return (
                  (a.roomPronunciation.localeCompare(b.roomPronunciation) ||
                    a.roomName.localeCompare(b.roomName) ||
                    a.bedPronunciation.localeCompare(b.bedPronunciation) ||
                    a.bedName.localeCompare(b.bedName)) * (descendingOrder ? -1 : 1)
                );
              case "bedName":
                return (
                  (a.bedName.localeCompare(b.bedName) ||
                    a.bedPronunciation.localeCompare(b.bedPronunciation) ||
                    a.roomName.localeCompare(b.roomName) ||
                    a.roomPronunciation.localeCompare(b.roomPronunciation)) *
                  (descendingOrder ? -1 : 1)
                );
              case "bedPronunciation":
                return (
                  (a.bedPronunciation.localeCompare(b.bedPronunciation) ||
                    a.bedName.localeCompare(b.bedName) ||
                    a.roomPronunciation.localeCompare(b.roomPronunciation) ||
                    a.roomName.localeCompare(b.roomName)) * (descendingOrder ? -1 : 1)
                );
              default:
                return 0;
            }
          }) ?? [];
      return {
        beds: beds.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize),
        numberOfBeds: beds.length,
      };
    },
    [bedInfiniteQuery.data, descendingOrder, orderBy, pageNumber, pageSize, roomName],
  );

  return (
    <main
      className={css({
        display: "flex",
        flexDirection: "column",
        padding: { smDown: "24px 16px", sm: "16px 24px" },
      })}
      data-maximized
    >
      <PageName>
        {t("%BedRegistration.0%") +
          " - " +
          (facilityQuery.data?.name ??
            (!facilityQuery.isFetching ? t("%Unknown.0%") : t("%Loading.0%")))}
      </PageName>

      <h1
        className={css(TypographyStyle.raw({ ellipsis: true }), {
          fontSize: { smDown: "18px", sm: "20px" },
          fontWeight: 700,
        })}
      >
        {t("%BedRegistration.0%")}
      </h1>

      {bedInfiniteQuery.isLoading || numberOfBeds > 0 || roomName ? (
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            marginTop: { smDown: "23px", sm: "16px" },
            minHeight: 0,
          })}
        >
          <div
            className={css({
              alignItems: "end",
              display: "grid",
              gridTemplateAreas: { smDown: '"a b" "c c"', sm: '"c b"' },
              gridTemplateColumns: "repeat(2, auto)",
              gap: "8px",
            })}
          >
            <div className={css({ hideFrom: "sm", gridArea: "a" })}>
              <SearchButton
                onClick={function () {
                  setOpen(function (open) {
                    return !open;
                  });
                }}
                open={open}
              />
            </div>
            <div
              className={css({
                gridArea: "c",
                '&[data-open="false"]': { display: { smDown: "none" } },
              })}
              data-open={open}
            >
              <FormField
                className={css({ maxWidth: { smDown: "stretch", sm: "240px" }, width: "stretch" })}
                label={t("%RoomName.0%")}
              >
                <FormFieldInput
                  className={css(FormFieldInputStyle.raw({ backgroundColor: "white" }), {
                    width: "100%",
                  })}
                  defaultValue={roomName}
                  onChange={function (event) {
                    debounce(function () {
                      const roomName = event.target.value || undefined;
                      navigate({
                        replace: true,
                        search(parameters) {
                          return { ...parameters, roomName };
                        },
                        to: ".",
                      });
                    });
                  }}
                  placeholder={t("%Search.0%")}
                />
              </FormField>
            </div>
            <div className={css({ gridArea: "b", justifySelf: "end" })}>
              <Link
                className={css(
                  ButtonStyle.raw({
                    appearance: "ghost",
                    borderRadius: "4px",
                    color: "ghost",
                    fontSize: "14px",
                    gap: "8px",
                    minWidth: 0,
                    padding: "10px 16px",
                  }),
                )}
                params={{ facilityKey }}
                to="/facilities/$facilityKey/beds/registration"
              >
                <LuPencil className={css({ height: "16px", width: "16px" })} />
                <span className={TypographyStyle({ ellipsis: true })}>{t("%Edit.0%")}</span>
              </Link>
            </div>
          </div>

          <div
            className={css(TABLE_STYLE, {
              flexGrow: 1,
              gridTemplateColumns: {
                smDown: "repeat(2, minmax(148px, 1fr))",
                sm: "repeat(4, minmax(148px, 1fr))",
              },
              marginTop: "6px",
              marginBottom: "auto",
              maxHeight: "max-content",
            })}
          >
            <div data-part="head" data-scope="table">
              <div data-part="head-row" data-scope="table">
                <TableHeadCell
                  descendingOrder={orderBy === "roomName" ? descendingOrder : null}
                  onClick={function () {
                    setOrderBy("roomName");
                    setDescendingOrder(orderBy === "roomName" ? !descendingOrder : false);
                  }}
                >
                  {t("%RoomName.0%")}
                </TableHeadCell>
                <TableHeadCell
                  data-hide-below="sm"
                  descendingOrder={orderBy === "roomPronunciation" ? descendingOrder : null}
                  onClick={function () {
                    setOrderBy("roomPronunciation");
                    setDescendingOrder(orderBy === "roomPronunciation" ? !descendingOrder : false);
                  }}
                >
                  {t("%RoomPronunciation.0%")}
                </TableHeadCell>
                <TableHeadCell
                  descendingOrder={orderBy === "bedName" ? descendingOrder : null}
                  onClick={function () {
                    setOrderBy("bedName");
                    setDescendingOrder(orderBy === "bedName" ? !descendingOrder : false);
                  }}
                >
                  {t("%BedName.0%")}
                </TableHeadCell>
                <TableHeadCell
                  data-hide-below="sm"
                  descendingOrder={orderBy === "bedPronunciation" ? descendingOrder : null}
                  onClick={function () {
                    setOrderBy("bedPronunciation");
                    setDescendingOrder(orderBy === "bedPronunciation" ? !descendingOrder : false);
                  }}
                >
                  {t("%BedPronunciation.0%")}
                </TableHeadCell>
              </div>
            </div>

            <div data-part="body" data-scope="table">
              {beds.map(function (bed) {
                return (
                  <div data-part="body-row" data-scope="table" key={bed.bedKey}>
                    <div data-part="body-cell" data-scope="table">
                      {bed.roomName}
                    </div>
                    <div data-hide-below="sm" data-part="body-cell" data-scope="table">
                      {bed.roomPronunciation}
                    </div>
                    <div data-part="body-cell" data-scope="table">
                      {bed.bedName}
                    </div>
                    <div data-hide-below="sm" data-part="body-cell" data-scope="table">
                      {bed.bedPronunciation}
                    </div>
                  </div>
                );
              })}

              {bedInfiniteQuery.isFetching &&
                Array.from({ length: Math.max(pageSize - beds.length, 0) }, function (_, index) {
                  const string1 = "　　　　";
                  const string2 = string1.repeat(2);
                  return (
                    <div data-part="body-row" data-scope="table" key={index}>
                      <div data-part="body-cell" data-scope="table">
                        <SkeletonScreen>{string1}</SkeletonScreen>
                      </div>
                      <div data-hide-below="sm" data-part="body-cell" data-scope="table">
                        <SkeletonScreen>{string2}</SkeletonScreen>
                      </div>
                      <div data-part="body-cell" data-scope="table">
                        <SkeletonScreen>{string1}</SkeletonScreen>
                      </div>
                      <div data-hide-below="sm" data-part="body-cell" data-scope="table">
                        <SkeletonScreen>{string2}</SkeletonScreen>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {numberOfBeds === 0 && roomName && (
            <div
              className={css({
                alignItems: "center",
                display: "flex",
                flexGrow: 1,
                justifyContent: "center",
                marginTop: "6px",
                minHeight: 0,
              })}
            >
              <p className={TypographyStyle({ ellipsis: true, fontSize: "14px" })}>
                {t("%NoData.4%")}
              </p>
            </div>
          )}

          <div
            className={css({ display: "flex", justifyContent: "space-between", marginTop: "8px" })}
          >
            <PageNumberSelector
              hasNextPage={bedInfiniteQuery.hasNextPage}
              isFetching={bedInfiniteQuery.isFetching}
              numberOfItems={numberOfBeds}
              onNextPage={function () {
                if (bedInfiniteQuery.hasNextPage && !bedInfiniteQuery.isLoading) {
                  setPageNumber(function (pageNumber) {
                    return pageNumber + 1;
                  });
                  bedInfiniteQuery.fetchNextPage().then(function (bedInfiniteQuery) {
                    setPageNumber(function (pageNumber) {
                      const numberOfBeds =
                        bedInfiniteQuery.data?.pages.reduce(function (numberOfBeds, page) {
                          return numberOfBeds + page.beds.length;
                        }, 0) ?? 0;
                      return Math.max(
                        Math.min(pageNumber + 1, Math.ceil(numberOfBeds / pageSize) - 1),
                        0,
                      );
                    });
                  });
                }
              }}
              pageNumber={pageNumber}
              pageSize={pageSize}
              setPageNumber={setPageNumber}
            />
            <PageSizeSelector
              onChange={function (pageSize) {
                setPageNumber(0);
                navigate({
                  replace: true,
                  search(parameters) {
                    return { ...parameters, pageSize };
                  },
                  to: ".",
                });
              }}
              options={
                new Set(
                  [pageSize, 50, 100, 150].sort(function (a, b) {
                    return a - b;
                  }),
                )
              }
              value={pageSize}
            />
          </div>
        </div>
      ) : (
        <div
          className={css({
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: { sm: "center" },
            justifyContent: "center",
            gap: "12px",
          })}
        >
          <p
            className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: "10 / 7" })}
          >
            {t("%NotRegistered.0%", { $: { name: t("%Bed.1%") } })}
          </p>
          <Link
            className={ButtonStyle({
              appearance: "contained",
              borderRadius: "4px",
              color: "primary",
              ellipsis: true,
              fontSize: "14px",
              gap: "8px",
              lineHeight: "10 / 7",
              padding: "10px 16px",
            })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/beds/registration"
          >
            {t("%Register.4%", { $: { name: t("%Bed.1%") } })}
          </Link>
        </div>
      )}
    </main>
  );
}
